import React, { useState, useEffect } from 'react';
import { FaUndo, FaSearch, FaEye, FaCalendar, FaPlus, FaTrash } from 'react-icons/fa';
import { usePageTitle } from '../hooks/usePageTitle';
import { useHiddenMode } from '../contexts/HiddenModeContext';
import { useAuth } from '../contexts/AuthContext';
import { useDebouncedSearch } from '../hooks/useDebounce';
import { projectService, Project as ProjectType } from '../services/projectService';
import { cancelReceivableIncomeService, CancelledReceivableIncome, CancelReceivableTransaction } from '../services/cancelReceivableIncomeService';
import Pagination from '../components/Pagination';
import '../css/pages/ReturnCancelIncome.css';

const CancelReceivableIncome: React.FC = () => {
  usePageTitle();
  const { isHiddenModeActive } = useHiddenMode();
  const { user } = useAuth();

  const [cancelledIncomes, setCancelledIncomes] = useState<CancelledReceivableIncome[]>([]);
  const [projects, setProjects] = useState<ProjectType[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showAddCancelModal, setShowAddCancelModal] = useState(false);
  const [showViewTransactionsModal, setShowViewTransactionsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<CancelledReceivableIncome | null>(null);
  const [transactions, setTransactions] = useState<CancelReceivableTransaction[]>([]);
  const [cancelFormData, setCancelFormData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);
  const itemsPerPage = 20;

  // Debounced search for unit number
  const {
    debouncedSearchTerm,
    getAbortSignal
  } = useDebouncedSearch(searchTerm, 350);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Hidden mode check is handled in render below

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (isHiddenModeActive) {
      loadCancelledIncomes();
    }
  }, [currentPage, debouncedSearchTerm, selectedProject, isHiddenModeActive]);

  const loadProjects = async () => {
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
      
      // Auto-select first project
      if (projectsData.length > 0) {
        setSelectedProject(projectsData[0].id.toString());
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadCancelledIncomes = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...(selectedProject && { projectId: selectedProject }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm })
      };

      // Get abort signal for request cancellation
      const abortSignal = getAbortSignal();

      const data = await cancelReceivableIncomeService.searchCancelledReceivableBookings(params, abortSignal || undefined);
      if (data.success) {
        setCancelledIncomes(data.data.bookings);
        setTotalPages(data.data.pagination.totalPages);
        setTotalCount(data.data.pagination.total);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        console.log('Request cancelled');
        return;
      }
      console.error('Error loading cancelled receivable incomes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (bookingId: string) => {
    try {
      const data = await cancelReceivableIncomeService.getReceivableBookingTransactions(bookingId);
      if (data.success) {
        setTransactions(data.data?.transactions || []);
      } else {
        // Handle API error gracefully
        console.warn('No transactions found:', data.message || 'Unknown error');
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString.trim() === '') {
      return '-';
    }
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '-';
      }
      return date.toLocaleDateString('en-IN');
    } catch (error) {
      return '-';
    }
  };

  const handleSaveCancelAmount = async () => {
    if (!selectedBooking) return;
    
    try {
      setSaving(true);
      
      const cancelData = {
        bookingId: selectedBooking.id,
        amount: parseFloat(cancelFormData.amount),
        date: cancelFormData.date,
        transactionType: 'cancel_refund'
      };

      const result = await cancelReceivableIncomeService.addReceivableCancelAmount(cancelData);
      
      if (result.success) {
        // Reset form
        setCancelFormData({
          amount: '',
          date: new Date().toISOString().split('T')[0]
        });
        setShowAddCancelModal(false);
        setSelectedBooking(null);
        
        // Reload the data to show updated amounts
        loadCancelledIncomes();
        
        alert('Cancel amount added successfully!');
      } else {
        alert('Error adding cancel amount: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving cancel amount:', error);
      alert('Error adding cancel amount. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetCancelForm = () => {
    setCancelFormData({
      amount: '',
      date: new Date().toISOString().split('T')[0]
    });
    setSelectedBooking(null);
    setShowAddCancelModal(false);
  };

  // Since API already handles search filtering, we don't need to filter again on frontend
  // The cancelledIncomes already contains the filtered results from the API
  const filteredIncomes = cancelledIncomes;

  // Don't render if hidden mode is not active
  if (!isHiddenModeActive) {
    return (
      <div className="unauthorized-access" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        padding: '2rem'
      }}>
        <div className="unauthorized-content" style={{
          textAlign: 'center',
          maxWidth: '500px',
          padding: '2rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginBottom: '1rem', color: '#dc3545' }}>🔒 Access Restricted</h2>
          <p style={{ marginBottom: '0.5rem', color: '#6c757d' }}>Cancel Receivable Income is only available when Hidden Mode is active.</p>
          <p style={{ color: '#6c757d' }}>Please activate Hidden Mode to access these features.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="return-cancel-income-page">

      {/* Search and Filters */}
      <div className="search-filters">
        <div className="filters-row">
          <div className="project-filter">
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id.toString()}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          <div className="search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by booking ID, customer name, or unit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon received">
            <FaCalendar />
          </div>
          <div className="card-content">
            <h3>Total Received</h3>
            <p className="amount">{formatCurrency(filteredIncomes.reduce((sum, income) => sum + income.totalReceivedAmount, 0))}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon refund">
            <FaUndo />
          </div>
          <div className="card-content">
            <h3>Total Given</h3>
            <p className="amount">{formatCurrency(filteredIncomes.reduce((sum, income) => sum + income.givenCancelAmount, 0))}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon retained">
            <FaEye />
          </div>
          <div className="card-content">
            <h3>Total Pending</h3>
            <p className="amount">{formatCurrency(filteredIncomes.reduce((sum, income) => sum + (income.totalReceivedAmount - income.givenCancelAmount), 0))}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon total">
            <FaEye />
          </div>
          <div className="card-content">
            <h3>Total Cancelled</h3>
            <p className="count">{filteredIncomes.length}</p>
          </div>
        </div>
      </div>

      {/* Cancelled Incomes Table */}
      <div className="cancel-receivable-table-container">
        {loading ? (
          <div className="loading">Loading cancelled receivable incomes...</div>
        ) : (
          <table className="cancelled-incomes-table">
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Booking ID</th>
                <th>Customer</th>
                <th>Project</th>
                <th>Unit & Wing</th>
                <th>Total Receivable Income</th>
                <th>Total Received</th>
                <th>Given Cancel Amount</th>
                <th>Pending Cancel Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredIncomes.map((income, index) => (
                <tr key={income.id}>
                  <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                    {filteredIncomes.length - index}
                  </td>
                  <td>{income.bookingId}</td>
                  <td>{income.customerName}</td>
                  <td>{income.project.name}</td>
                  <td>{income.unitDisplay}</td>
                  <td className="amount" style={{color: income.totalHiddenIncomeAmount < 0 ? '#dc2626' : '#059669'}}>
                    {formatCurrency(income.totalHiddenIncomeAmount)}
                  </td>
                  <td className="amount received" style={{color: income.totalReceivedAmount < 0 ? '#dc2626' : '#059669'}}>
                    {formatCurrency(income.totalReceivedAmount)}
                  </td>
                  <td className="amount refund" style={{color: income.givenCancelAmount < 0 ? '#dc2626' : '#059669'}}>
                    {formatCurrency(income.givenCancelAmount)}
                  </td>
                  <td className="amount pending" style={{color: (income.totalReceivedAmount - income.givenCancelAmount) < 0 ? '#dc2626' : '#059669'}}>
                    {formatCurrency(income.totalReceivedAmount - income.givenCancelAmount)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view"
                        title="View Transactions"
                        onClick={() => {
                          setSelectedBooking(income);
                          loadTransactions(income.id);
                          setShowViewTransactionsModal(true);
                        }}
                      >
                        <FaEye />
                      </button>
                      <button 
                        className="action-btn add"
                        title="Add Cancel Amount"
                        onClick={() => {
                          setSelectedBooking(income);
                          setShowAddCancelModal(true);
                        }}
                      >
                        <FaPlus />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        loading={loading}
      />

      {/* Add Cancel Amount Modal */}
      {showAddCancelModal && (
        <div className="modal-overlay" onClick={() => setShowAddCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Cancel Amount</h2>
              <button 
                className="modal-close"
                onClick={() => setShowAddCancelModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {selectedBooking && (
                <div className="booking-info">
                  <h3>Booking Details</h3>
                  <p><strong>Booking ID:</strong> {selectedBooking.bookingId}</p>
                  <p><strong>Customer:</strong> {selectedBooking.customerName}</p>
                  <p><strong>Project:</strong> {selectedBooking.project.name}</p>
                  <p><strong>Unit:</strong> {selectedBooking.unitDisplay}</p>
                </div>
              )}
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Cancel Amount *</label>
                  <input 
                    type="number" 
                    placeholder="Enter cancel amount"
                    value={cancelFormData.amount}
                    onChange={(e) => setCancelFormData({...cancelFormData, amount: e.target.value})}
                    onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input 
                    type="date" 
                    value={cancelFormData.date}
                    onChange={(e) => setCancelFormData({...cancelFormData, date: e.target.value})}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={resetCancelForm}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleSaveCancelAmount}
                disabled={saving || !cancelFormData.amount || !cancelFormData.date}
              >
                {saving ? 'Saving...' : 'Save Cancel Amount'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Transactions Modal */}
      {showViewTransactionsModal && (
        <div className="modal-overlay" onClick={() => setShowViewTransactionsModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cancel Transactions</h2>
              <button 
                className="modal-close"
                onClick={() => setShowViewTransactionsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {selectedBooking && (
                <div className="booking-info">
                  <h3>Booking Details</h3>
                  <p><strong>Booking ID:</strong> {selectedBooking.bookingId}</p>
                  <p><strong>Customer:</strong> {selectedBooking.customerName}</p>
                  <p><strong>Project:</strong> {selectedBooking.project.name}</p>
                  <p><strong>Unit:</strong> {selectedBooking.unitDisplay}</p>
                  <p><strong>Total Received:</strong> {formatCurrency(selectedBooking.totalReceivedAmount)}</p>
                  <p><strong>Final Cancel Amount:</strong> {formatCurrency(selectedBooking.finalCancelAmount)}</p>
                  <p><strong>Given Cancel Amount:</strong> {formatCurrency(selectedBooking.givenCancelAmount)}</p>
                  <p><strong>Pending Cancel Amount:</strong> {formatCurrency(selectedBooking.pendingCancelAmount)}</p>
                </div>
              )}
              
              <div className="transactions-section">
                <h3>Refund Transactions</h3>
                {transactions.length > 0 ? (
                  <div className="transactions-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Payment Method</th>
                          <th>Reference</th>
                          <th>Processed By</th>
                          <th>Remarks</th>
                          {user?.isSuperAdmin && <th style={{ width: '50px', textAlign: 'center' }}>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td>{transaction.date || transaction.refundDate || '-'}</td>
                            <td className="amount" style={{color: transaction.amount < 0 ? '#dc2626' : '#059669'}}>
                              {formatCurrency(transaction.amount)}
                            </td>
                            <td>{transaction.paymentMethod}</td>
                            <td>{transaction.referenceNumber || '-'}</td>
                            <td>{transaction.processedBy}</td>
                            <td>{transaction.remarks}</td>
                            {user?.isSuperAdmin && (
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Are you sure you want to delete this transaction of ${formatCurrency(transaction.amount)}?`)) return;
                                    try {
                                      await cancelReceivableIncomeService.deleteTransaction(transaction.id, (transaction.type as 'income' | 'refund') || 'refund');
                                      setTransactions(prev => prev.filter(t => t.id !== transaction.id));
                                      loadCancelledIncomes();
                                    } catch (err) {
                                      alert('Failed to delete transaction');
                                    }
                                  }}
                                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '4px' }}
                                  title="Delete transaction"
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-transactions">No refund transactions found for this booking.</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowViewTransactionsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancelReceivableIncome;